import { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Switch, Tag, theme, message, Button } from '../../ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { FileUploadField } from '../../components/FileUploadField';
import { fmtUom } from '../../lib/format';
import { usePermission } from '../../store/auth';

/**
 * Add a new variant LINKED to an existing master product. The backend inherits
 * the master's shared fields (name, HSN, UoM, description, catalogue) and groups
 * them by the master's group_code, so the variant is always tied to its master.
 *
 * Same as the product-creation variant editor: you only type a NUMBER (pack size);
 * the UoM is shown as a unit suffix and the variant label is auto-previewed
 * (e.g. 1 → "1L"). No free-text label typing.
 */
const UOM_SUFFIX: Record<string, string> = { NOS: 'Nos', KG: 'Kg', LTR: 'L', BAG: 'Bag', BOX: 'Box' };

export function AddVariantModal({ master, onClose }: { master: any | null; onClose: () => void }) {
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const open = !!master;
  const masterId = master?.id ?? master?.productId;
  const uom = (master?.uom as string) || 'NOS';
  const unit = UOM_SUFFIX[uom] ?? uom;

  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const packSize = Form.useWatch('packSize', form);
  const canEditPackaging = usePermission('PRODUCT.PACKAGING.EDIT');
  const boxEnabled = Form.useWatch('boxPackingEnabled', form);
  const previewLabel =
    packSize === undefined || packSize === null || (packSize as any) === '' ? '' : fmtUom(packSize, uom);

  useEffect(() => { if (open) setImageUrl(undefined); }, [open, masterId]);

  const save = useConfirmedMutation({ title: "Save this variant?", content: "It becomes orderable as soon as it is saved.", okText: "Yes, save" }, {
    mutationFn: (v: any) =>
      request({
        url: `/products/${masterId}/variants`,
        method: 'POST',
        data: {
          sku: v.sku?.trim(),
          variantLabel: fmtUom(v.packSize, uom),
          mrp: Number(v.mrp),
          dealerPrice: Number(v.dealerPrice),
          packSize: Number(v.packSize),
          moq: v.moq != null ? Number(v.moq) : undefined,
          imageUrl: imageUrl || undefined,
          ...(canEditPackaging && v.boxPackingEnabled ? { boxPackingEnabled: true, unitsPerBox: Number(v.unitsPerBox), boxBarcode: v.boxBarcode || undefined, boxSku: v.boxSku || undefined } : {}),
        },
      }),
    onSuccess: () => {
      message.success('Variant added');
      qc.invalidateQueries({ queryKey: ['catalog-with-stock'] });
      qc.invalidateQueries({ queryKey: ['catalog-facets'] });
      qc.invalidateQueries({ queryKey: ['products-list'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to add variant'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Add variant"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Add variant</Button>
      </>}
    >
      <div style={{ marginBottom: 16, padding: '10px 12px', borderRadius: 8, background: token.colorFillQuaternary, fontSize: 13 }}>
        Linking a new variant to <b>{master?.name}</b>
        {master?.groupCode ? <> · group <Tag style={{ margin: 0 }}>{master.groupCode}</Tag></> : null}.
        It inherits the master's name, HSN, UoM (<b>{uom}</b>), description and catalogue.
      </div>
      <Form form={form} layout="vertical" initialValues={{ packSize: 1, moq: 1 }} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="sku" label="Variant SKU" rules={[{ required: true, message: 'SKU is required' }]}>
          <Input placeholder={`${master?.sku ?? master?.groupCode ?? 'GBB'}-…`} autoFocus />
        </Form.Item>
        <Form.Item
          name="packSize"
          label="Pack size"
          rules={[{ required: true, message: 'Pack size is required' }, { type: 'number', min: 0.001, message: 'Enter a valid quantity' }]}
          style={{ marginBottom: 6 }}
        >
          <InputNumber style={{ width: '100%' }} min={0.001} step={0.5} addonAfter={unit} placeholder="e.g. 1" />
        </Form.Item>
        <div style={{ marginBottom: 18, fontSize: 13, color: token.colorTextSecondary }}>
          Variant label: <b style={{ color: token.colorText }}>{previewLabel || '—'}</b>
          <span style={{ color: token.colorTextTertiary }}> · auto from pack size</span>
        </div>
        <Form.Item name="mrp" label="MRP (₹)" rules={[{ required: true, message: 'MRP is required' }]}>
          <InputNumber style={{ width: '100%' }} min={0} step={10} />
        </Form.Item>
        <Form.Item name="dealerPrice" label="Dealer price (₹)"
          rules={[{ required: true, message: 'Dealer price is required' }, { type: 'number', min: 0.01, message: 'Enter a valid price' }]}>
          <InputNumber style={{ width: '100%' }} min={0} step={10} placeholder="e.g. 90" />
        </Form.Item>
        <Form.Item name="moq" label="MOQ">
          <InputNumber style={{ width: '100%' }} min={0.001} step={1} />
        </Form.Item>
        {canEditPackaging && (
          <>
            <Form.Item name="boxPackingEnabled" label="Box packing" valuePropName="checked" style={{ marginBottom: 8 }} extra="How many saleable units are in 1 box (a count — not derived from the size)">
              <Switch />
            </Form.Item>
            {boxEnabled && (
              <Form.Item name="unitsPerBox" label="Units per box" rules={[{ required: true, message: 'Required when box packing is on' }, { type: 'integer', min: 1, message: 'Whole number greater than 0' }]}>
                <InputNumber style={{ width: '100%' }} min={1} step={1} precision={0} placeholder="e.g. 10" />
              </Form.Item>
            )}
          </>
        )}
        <Form.Item label="Variant image (optional)">
          <FileUploadField
            category="product-image"
            preview={false}
            buttonLabel={imageUrl ? 'Replace' : 'Upload'}
            value={imageUrl}
            onChange={(url) => setImageUrl(url)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
